import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminApiService from '../../services/adminApiService';

// Initial empty form state
const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  city: '',
  state: '',
  zip: '',
  country: '',
};

const AdminAddSupportExecutive = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Generic change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear field-level error on change
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFieldErrors({});

    try {
      const response = await adminApiService.addSupportExecutive(form);

      if (response.success) {
        toast.success(response.message || 'Support executive added successfully.');
        setForm(INITIAL_FORM);
      } else {
        // Server returned validation errors map
        if (response.errors) {
          setFieldErrors(response.errors);
          toast.error(response.message || 'Please fix the highlighted errors.');
        } else {
          toast.error(response.message || 'Failed to add support executive.');
        }
      }
    } catch (error) {
      // makeSupportApiCall throws on non-2xx; try to parse field errors from error body
      toast.error(error.message || 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-3xl">

        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add Support Executive</h1>
            <p className="text-gray-600 mt-1">Create a new support executive account. Login credentials will be emailed automatically.</p>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard/settings')}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm"
          >
            ← Back to Settings
          </button>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Executive Details</h3>
          </div>

          <form onSubmit={handleSubmit} noValidate className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              <Field
                label="First Name"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                error={fieldErrors.firstName}
                placeholder="e.g. Rahul"
              />

              <Field
                label="Last Name"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                error={fieldErrors.lastName}
                placeholder="e.g. Sharma"
              />

              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                error={fieldErrors.email}
                placeholder="e.g. rahul@example.com"
              />

              <Field
                label="Phone Number"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                error={fieldErrors.phoneNumber}
                placeholder="10-digit mobile number"
                maxLength={10}
              />

              <Field
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
                error={fieldErrors.city}
                placeholder="e.g. Mumbai"
              />

              <Field
                label="State"
                name="state"
                value={form.state}
                onChange={handleChange}
                error={fieldErrors.state}
                placeholder="e.g. Maharashtra"
              />

              <Field
                label="ZIP Code"
                name="zip"
                value={form.zip}
                onChange={handleChange}
                error={fieldErrors.zip}
                placeholder="6-digit ZIP"
                maxLength={6}
              />

              <Field
                label="Country"
                name="country"
                value={form.country}
                onChange={handleChange}
                error={fieldErrors.country}
                placeholder="e.g. India"
              />

            </div>

            {/* Info note */}
            <p className="mt-6 text-sm text-gray-500 bg-blue-50 border border-blue-200 rounded-md px-4 py-3">
              A secure password will be auto-generated and sent to the executive's email. They will be prompted to change it on first login.
            </p>

            {/* Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => { setForm(INITIAL_FORM); setFieldErrors({}); }}
                className="px-5 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-medium disabled:opacity-50 flex items-center"
              >
                {submitting ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Saving...
                  </>
                ) : 'Add Support Executive'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Reusable field component
const Field = ({ label, name, type = 'text', value, onChange, error, placeholder, maxLength }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label} <span className="text-red-500">*</span>
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxLength={maxLength}
      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 ${
        error
          ? 'border-red-400 focus:ring-red-400 bg-red-50'
          : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'
      }`}
    />
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

export default AdminAddSupportExecutive;
